import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react'
import {
  Search, X, SortAsc, Clock, HardDrive, Shuffle,
  RefreshCw, ListPlus, CornerDownRight, Trash2, ChevronUp, ChevronDown,
  Save, List, CheckSquare, Square, Plus, Star, Heart,
  Info, Cloud, PlayCircle, CalendarDays, MonitorPlay, XCircle, EyeOff,
} from 'lucide-react'
import { usePlaybackStore, useSettingsStore } from '../../store'
import { ratingApi, roomApi, savedPlaylistApi, videoApi } from '../../services/api'
import {
  formatDuration, formatBytes, formatRelativeDate, cleanFilename,
  getCompatibilityInfo,
} from '../../utils'
import type { BumperPlacement, BumperRule, CompatibilityStatus, PlaylistItem, SavedPlaylist, SortOrder, VideoRating } from '../../types'
import clsx from 'clsx'
import { VideoThumbnail } from './VideoThumbnail'

interface PlaylistSidebarProps {
  onRefresh: () => void
  isRefreshing: boolean
  refreshError: string | null
  totalSize: number
  isOwner: boolean
}

const SORT_OPTIONS: { value: SortOrder; label: string; icon: React.ReactNode }[] = [
  { value: 'name', label: 'Name', icon: <SortAsc size={13} /> },
  { value: 'date', label: 'Date', icon: <Clock size={13} /> },
  { value: 'size', label: 'Size', icon: <HardDrive size={13} /> },
  { value: 'shuffle', label: 'Shuffle', icon: <Shuffle size={13} /> },
]

export function PlaylistSidebar({ onRefresh, isRefreshing, refreshError, totalSize, isOwner }: PlaylistSidebarProps) {
  const [savedPlaylists, setSavedPlaylists] = useState<SavedPlaylist[]>([])
  const [playlistName, setPlaylistName] = useState('')
  const [savedPlaylistsLoading, setSavedPlaylistsLoading] = useState(false)
  const [savedPlaylistError, setSavedPlaylistError] = useState<string | null>(null)
  const [isSelectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [ratings, setRatings] = useState<Map<string, VideoRating>>(() => new Map())
  const [showLiked, setShowLiked] = useState(false)
  const [ratingError, setRatingError] = useState<string | null>(null)
  const [inspectedItem, setInspectedItem] = useState<PlaylistItem | null>(null)
  const {
    filteredPlaylist, playlist, currentIndex, currentVideoId, activeSavedPlaylistId,
    searchQuery, sortOrder, queueIds,
    setCurrentVideo, setSearchQuery, setSortOrder, setStatus, setPlaylist,
    addToQueue, addToQueueNext, addManyToQueue, removeQueuedVideo, moveQueuedVideo,
    playQueuedVideo, clearQueue, setQueue, setCurrentVideoById, setActiveSavedPlaylist, setBumperRule,
    resetBumperSequence, nextVideo,
  } = usePlaybackStore()
  const settings = useSettingsStore((state) => state.settings)
  const updateSettings = useSettingsStore((state) => state.updateSettings)

  const activeRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const appliedDefaultRef = useRef<string | null>(null)
  const videoById = useMemo(
    () => new Map(playlist.map((item) => [item.id, item])),
    [playlist],
  )
  const queuedVideos = useMemo(
    () => queueIds
      .map((videoId, queueIndex) => ({
        item: videoById.get(videoId) ?? null,
        queueIndex,
      }))
      .filter((entry): entry is { item: NonNullable<typeof entry.item>; queueIndex: number } =>
        entry.item !== null,
      ),
    [queueIds, videoById],
  )
  const selectedVideoIds = useMemo(
    () => playlist
      .filter((item) => selectedIds.has(item.id))
      .map((item) => item.id),
    [playlist, selectedIds],
  )
  const saveSourceIds = selectedVideoIds.length > 0 ? selectedVideoIds : queueIds
  const saveSourceLabel = selectedVideoIds.length > 0 ? 'selection' : 'queue'
  const ratingKey = useCallback((item: Pick<PlaylistItem, 'id' | 'provider'>) => `${item.provider}:${item.id}`, [])
  const likedCount = useMemo(
    () => playlist.filter((item) => ratings.get(ratingKey(item))?.liked).length,
    [playlist, ratingKey, ratings],
  )
  const visiblePlaylist = useMemo(
    () => showLiked ? filteredPlaylist.filter((item) => ratings.get(ratingKey(item))?.liked) : filteredPlaylist,
    [filteredPlaylist, ratingKey, ratings, showLiked],
  )

  // Scroll active item into view
  useEffect(() => {
    if (activeRef.current && listRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [currentIndex])

  useEffect(() => {
    const validIds = new Set(playlist.map((item) => item.id))
    setSelectedIds((current) => {
      const next = new Set([...current].filter((videoId) => validIds.has(videoId)))
      return next.size === current.size ? current : next
    })
  }, [playlist])

  const loadSavedPlaylists = useCallback(async () => {
    if (playlist.length === 0) return
    setSavedPlaylistsLoading(true)
    setSavedPlaylistError(null)

    try {
      setSavedPlaylists(await savedPlaylistApi.list())
    } catch (error) {
      setSavedPlaylistError(error instanceof Error ? error.message : 'Could not load saved playlists')
    } finally {
      setSavedPlaylistsLoading(false)
    }
  }, [playlist.length])

  useEffect(() => {
    void loadSavedPlaylists()
  }, [loadSavedPlaylists])

  useEffect(() => {
    if (!settings?.roomId || playlist.length === 0) return
    let cancelled = false
    setRatingError(null)
    void ratingApi.list()
      .then((items) => {
        if (!cancelled) setRatings(new Map(items.map((item) => [`${item.provider}:${item.videoId}`, item])))
      })
      .catch((error) => {
        if (!cancelled) setRatingError(error instanceof Error ? error.message : 'Could not load ratings')
      })
    return () => { cancelled = true }
  }, [playlist.length, settings?.roomId])

  const updateRating = useCallback(async (
    item: PlaylistItem, patch: { rating?: number; liked?: boolean },
  ) => {
    const key = ratingKey(item)
    const previous = ratings.get(key) ?? { videoId: item.id, provider: item.provider, rating: 0, liked: false }
    setRatingError(null)
    setRatings((current) => new Map(current).set(key, { ...previous, ...patch }))
    try {
      const updated = await ratingApi.update(item.id, item.provider, patch)
      setRatings((current) => new Map(current).set(key, updated))
    } catch (error) {
      setRatings((current) => new Map(current).set(key, previous))
      setRatingError(error instanceof Error ? error.message : 'Could not save rating')
    }
  }, [ratingKey, ratings])

  const handleItemClick = useCallback((index: number) => {
    setActiveSavedPlaylist(null)
    setCurrentVideo(index)
    setStatus('loading')
  }, [setActiveSavedPlaylist, setCurrentVideo, setStatus])

  const toggleSelected = useCallback((videoId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(videoId)) {
        next.delete(videoId)
      } else {
        next.add(videoId)
      }
      return next
    })
  }, [])

  const selectVisibleVideos = useCallback(() => {
    setSelectedIds((current) => {
      const next = new Set(current)
      filteredPlaylist.forEach((item) => next.add(item.id))
      return next
    })
  }, [filteredPlaylist])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handlePlayQueuedVideo = useCallback((queueIndex: number) => {
    playQueuedVideo(queueIndex)
    setStatus('loading')
  }, [playQueuedVideo, setStatus])

  const handleSaveQueue = useCallback(async () => {
    const name = playlistName.trim()
    if (!name || saveSourceIds.length === 0) return

    setSavedPlaylistsLoading(true)
    setSavedPlaylistError(null)
    try {
      const saved = await savedPlaylistApi.create(name, saveSourceIds)
      setSavedPlaylists((items) => [saved, ...items])
      setPlaylistName('')
    } catch (error) {
      setSavedPlaylistError(error instanceof Error ? error.message : 'Could not save playlist')
    } finally {
      setSavedPlaylistsLoading(false)
    }
  }, [playlistName, saveSourceIds])

  const activateSavedPlaylist = useCallback((saved: SavedPlaylist, resume: boolean) => {
    setActiveSavedPlaylist(saved.id, saved.resume.revision, resume ? saved.resume.position : null)
    const rule = saved.bumperRule ?? (settings ? {
      enabled: settings.bumperEnabled, videoId: settings.bumperVideoId,
      placement: settings.bumperPlacement, frequency: settings.bumperFrequency,
      skipAllowed: settings.bumperSkipAllowed, volume: settings.bumperVolume,
    } : null)
    if (rule) setBumperRule(rule)
    resetBumperSequence(resume)
    if (resume && saved.resume.videoId) {
      setQueue(saved.resume.queueIds)
      setCurrentVideoById(saved.resume.videoId)
      return
    }
    if (rule?.enabled && rule.videoId && rule.placement === 'before_first') {
      setQueue(saved.itemIds)
      nextVideo()
      return
    }
    const [firstVideoId, ...remainingIds] = saved.itemIds
    setQueue(remainingIds)
    if (firstVideoId) setCurrentVideoById(firstVideoId)
  }, [nextVideo, resetBumperSequence, setActiveSavedPlaylist, setBumperRule, setCurrentVideoById, setQueue, settings])

  const handleLoadSavedPlaylist = useCallback((saved: SavedPlaylist) => {
    const canResume = Boolean(saved.resume.videoId) && !saved.resume.completedAt
    const resume = canResume && window.confirm(`Resume “${saved.name}” where you left off?`)
    activateSavedPlaylist(saved, resume)
  }, [activateSavedPlaylist])

  useEffect(() => {
    const defaultPlaylistId = settings?.defaultPlaylistId
    const roomId = settings?.roomId
    if (!defaultPlaylistId || !roomId || activeSavedPlaylistId) return
    const key = `${roomId}:${defaultPlaylistId}`
    if (appliedDefaultRef.current === key) return
    const saved = savedPlaylists.find((item) => item.id === defaultPlaylistId)
    if (!saved) return
    appliedDefaultRef.current = key
    activateSavedPlaylist(saved, false)
  }, [activateSavedPlaylist, activeSavedPlaylistId, savedPlaylists, settings?.defaultPlaylistId, settings?.roomId])

  const handleDefaultPlaylist = useCallback(async (saved: SavedPlaylist) => {
    if (!settings?.roomId) return
    const defaultPlaylistId = settings.defaultPlaylistId === saved.id ? null : saved.id
    try {
      await roomApi.update(settings.roomId, { defaultPlaylistId })
      updateSettings({ defaultPlaylistId })
      if (defaultPlaylistId) appliedDefaultRef.current = `${settings.roomId}:${defaultPlaylistId}`
    } catch (error) {
      setSavedPlaylistError(error instanceof Error ? error.message : 'Could not update the default playlist')
    }
  }, [settings?.defaultPlaylistId, settings?.roomId, updateSettings])

  const handleBumperOverride = useCallback(async (saved: SavedPlaylist, value: string) => {
    const base: BumperRule = {
      enabled: settings?.bumperEnabled ?? false,
      videoId: settings?.bumperVideoId ?? null,
      placement: settings?.bumperPlacement ?? 'after_every_n',
      frequency: settings?.bumperFrequency ?? 3,
      skipAllowed: settings?.bumperSkipAllowed ?? true,
      volume: settings?.bumperVolume ?? 1,
    }
    const rule = value === 'inherit' ? null : value === 'disabled'
      ? { ...base, enabled: false }
      : { ...base, enabled: true, placement: value as BumperPlacement }
    try {
      const updated = await savedPlaylistApi.saveBumperRule(saved.id, rule)
      setSavedPlaylists((items) => items.map((item) => item.id === saved.id ? { ...item, bumperRule: updated.bumperRule } : item))
    } catch (error) {
      setSavedPlaylistError(error instanceof Error ? error.message : 'Could not save bumper override')
    }
  }, [settings])

  const handleDeleteSavedPlaylist = useCallback(async (playlistId: string) => {
    setSavedPlaylistsLoading(true)
    setSavedPlaylistError(null)
    try {
      if (settings?.defaultPlaylistId === playlistId && settings.roomId) {
        await roomApi.update(settings.roomId, { defaultPlaylistId: null })
        updateSettings({ defaultPlaylistId: null })
      }
      await savedPlaylistApi.delete(playlistId)
      setSavedPlaylists((items) => items.filter((item) => item.id !== playlistId))
    } catch (error) {
      setSavedPlaylistError(error instanceof Error ? error.message : 'Could not delete playlist')
    } finally {
      setSavedPlaylistsLoading(false)
    }
  }, [settings?.defaultPlaylistId, settings?.roomId, updateSettings])

  const handleHideVideo = useCallback(async (item: PlaylistItem) => {
    if (!window.confirm(`Hide “${cleanFilename(item.name)}” from this room?\n\nThe source file will stay untouched in ${item.provider === 'google' ? 'Google Drive' : 'OneDrive'}.`)) return
    await videoApi.hideFromRoom(item.id, item.provider)
    setPlaylist(playlist.filter((candidate) => candidate.id !== item.id || candidate.provider !== item.provider))
    setInspectedItem(null)
  }, [playlist, setPlaylist])

  return (
    <div className="relative flex flex-col h-full bg-panel border-l border-border animate-slide-in">

      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border space-y-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-bright">Playlist</h2>
            <p className="text-xs text-muted mt-0.5">
              {playlist.length} videos · {formatBytes(totalSize)}
            </p>
          </div>
          {isOwner && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="btn-ghost p-2 rounded-lg"
              title="Refresh playlist"
            >
              <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
          )}
        </div>

        {refreshError && (
          <p role="alert" className="text-xs text-danger">
            {refreshError}
          </p>
        )}

        {ratingError && <p role="alert" className="text-xs text-danger">{ratingError}</p>}

        <button
          onClick={() => setShowLiked((value) => !value)}
          className={clsx(
            'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors',
            showLiked ? 'border-rose-400/40 bg-rose-400/10 text-rose-300' : 'border-border bg-surface/55 text-soft hover:bg-border/45',
          )}
          aria-pressed={showLiked}
        >
          <Heart size={14} fill={showLiked ? 'currentColor' : 'none'} />
          <span className="flex-1 text-xs font-semibold">Liked</span>
          <span className="text-[11px] font-mono opacity-70">{likedCount}</span>
        </button>

        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Search videos…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-8 pr-8 py-1.5 text-xs"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-soft"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {isOwner && (
          <div className="flex gap-1">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortOrder(opt.value)}
                className={clsx(
                  'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors flex-1 justify-center',
                  sortOrder === opt.value
                    ? 'bg-accent/20 text-accent'
                    : 'text-muted hover:text-soft hover:bg-border',
                )}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1">
          <button
            onClick={() => setSelectionMode((value) => !value)}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
              isSelectionMode
                ? 'bg-accent/20 text-accent'
                : 'text-muted hover:text-soft hover:bg-border',
            )}
          >
            {isSelectionMode ? <CheckSquare size={13} /> : <Square size={13} />}
            {isSelectionMode ? 'Done' : 'Select'}
          </button>

          {isSelectionMode && (
            <>
              <button
                onClick={selectVisibleVideos}
                className="btn-ghost px-2 py-1 rounded text-xs"
              >
                Visible
              </button>
              <button
                onClick={() => addManyToQueue(selectedVideoIds)}
                disabled={selectedVideoIds.length === 0}
                className="btn-ghost px-2 py-1 rounded text-xs disabled:opacity-45 disabled:cursor-default"
                title="Add selected to queue"
              >
                <Plus size={12} />
                Queue
              </button>
              <button
                onClick={clearSelection}
                disabled={selectedVideoIds.length === 0}
                className="btn-ghost px-2 py-1 rounded text-xs disabled:opacity-45 disabled:cursor-default"
              >
                Clear
              </button>
              <span className="ml-auto text-[11px] text-muted">
                {selectedVideoIds.length}
              </span>
            </>
          )}
        </div>
      </div>

      <section className="border-b border-border bg-panel flex-shrink-0">
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <List size={14} className="text-muted" />
              <h3 className="text-xs font-semibold text-bright">Saved playlists</h3>
            </div>
            <button
              onClick={() => void loadSavedPlaylists()}
              disabled={savedPlaylistsLoading || playlist.length === 0}
              className="btn-ghost px-2 py-1 rounded text-xs"
              title="Reload saved playlists"
            >
              Reload
            </button>
          </div>

          {isOwner && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={saveSourceIds.length > 0 ? `Name this ${saveSourceLabel}...` : 'Select or queue videos...'}
                value={playlistName}
                onChange={(event) => setPlaylistName(event.target.value)}
                disabled={saveSourceIds.length === 0}
                className="input py-1.5 text-xs"
              />
              <button
                onClick={() => void handleSaveQueue()}
                disabled={!playlistName.trim() || saveSourceIds.length === 0 || savedPlaylistsLoading}
                className="btn-ghost px-2.5 py-1.5 rounded border border-border disabled:opacity-45 disabled:cursor-default"
                title={`Save current ${saveSourceLabel} as playlist`}
              >
                <Save size={13} />
              </button>
            </div>
          )}

          {savedPlaylistError && (
            <p className="text-[11px] text-danger">{savedPlaylistError}</p>
          )}

          {savedPlaylists.length > 0 && (
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {savedPlaylists.map((saved) => (
                <div
                  key={saved.id}
                  className="flex items-center gap-2 rounded border border-border/60 bg-surface/55 px-2 py-1.5"
                >
                  {videoById.get(saved.itemIds[0]) && (
                    <VideoThumbnail item={videoById.get(saved.itemIds[0])!} compact />
                  )}
                  <button
                    onClick={() => handleLoadSavedPlaylist(saved)}
                    className="flex-1 min-w-0 text-left"
                    title="Load into queue"
                  >
                    <span className="block text-xs text-text truncate">{saved.name}</span>
                    <span className="block text-[11px] text-muted">
                      {saved.itemCount} videos
                    </span>
                  </button>
                  {isOwner && (
                    <button
                      onClick={() => void handleDefaultPlaylist(saved)}
                      className={clsx('btn-ghost rounded p-1', settings?.defaultPlaylistId === saved.id && 'text-accent')}
                      title={settings?.defaultPlaylistId === saved.id ? 'Remove room default' : 'Use as room default'}
                      aria-label={settings?.defaultPlaylistId === saved.id ? 'Remove room default' : 'Use as room default'}
                    >
                      <Star size={13} fill={settings?.defaultPlaylistId === saved.id ? 'currentColor' : 'none'} />
                    </button>
                  )}
                  {isOwner && (
                    <select
                      value={saved.bumperRule ? (saved.bumperRule.enabled ? saved.bumperRule.placement : 'disabled') : 'inherit'}
                      onChange={(event) => void handleBumperOverride(saved, event.target.value)}
                      className="input w-24 py-1 text-[10px]"
                      title="Playlist bumper rule"
                    >
                      <option value="inherit">Room rule</option>
                      <option value="disabled">No bumper</option>
                      <option value="before_first">Before</option>
                      <option value="after_every_n">Every N</option>
                      <option value="playlist_end">At end</option>
                    </select>
                  )}
                  {isOwner && (
                    <button
                      onClick={() => void handleDeleteSavedPlaylist(saved.id)}
                      disabled={savedPlaylistsLoading}
                      className="btn-ghost p-1 rounded"
                      title="Delete saved playlist"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {queuedVideos.length > 0 && (
        <section className="border-b border-border bg-surface/45 flex-shrink-0">
          <div className="px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold text-bright">Up next</h3>
              <p className="text-[11px] text-muted mt-0.5">
                {queuedVideos.length} queued
              </p>
            </div>
            <button
              onClick={clearQueue}
              className="btn-ghost px-2 py-1 rounded text-xs"
              title="Clear queue"
            >
              Clear
            </button>
          </div>

          <div className="max-h-56 overflow-y-auto">
            {queuedVideos.map(({ item, queueIndex }) => (
              <div
                key={`${item.id}-${queueIndex}`}
                className="flex items-start gap-2 px-4 py-2 border-t border-border/50 hover:bg-border/35"
              >
                <VideoThumbnail item={item} compact />
                <button
                  onClick={() => handlePlayQueuedVideo(queueIndex)}
                  className="w-6 flex-shrink-0 mt-0.5 text-right text-xs font-mono text-accent hover:text-bright"
                  title="Play queued video now"
                >
                  {queueIndex + 1}
                </button>

                <div className="flex-1 min-w-0">
                  <p className="text-xs leading-tight text-text truncate">
                    {cleanFilename(item.name)}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {item.durationSeconds && (
                      <span className="text-[11px] font-mono text-muted">
                        {formatDuration(item.durationSeconds)}
                      </span>
                    )}
                    <span className="text-[11px] text-muted">
                      {formatBytes(item.size)}
                    </span>
                    <CompatibilityBadge item={item} compact />
                  </div>
                </div>

                <div className="flex items-center gap-0.5 text-muted">
                  <button
                    onClick={() => moveQueuedVideo(queueIndex, -1)}
                    disabled={queueIndex === 0}
                    className="btn-ghost p-1 rounded disabled:opacity-35 disabled:cursor-default"
                    title="Move up"
                  >
                    <ChevronUp size={13} />
                  </button>
                  <button
                    onClick={() => moveQueuedVideo(queueIndex, 1)}
                    disabled={queueIndex === queuedVideos.length - 1}
                    className="btn-ghost p-1 rounded disabled:opacity-35 disabled:cursor-default"
                    title="Move down"
                  >
                    <ChevronDown size={13} />
                  </button>
                  <button
                    onClick={() => removeQueuedVideo(queueIndex)}
                    className="btn-ghost p-1 rounded"
                    title="Remove from queue"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {visiblePlaylist.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted text-sm gap-2">
            {showLiked ? <Heart size={24} strokeWidth={1} /> : <Search size={24} strokeWidth={1} />}
            <p>{showLiked ? 'No liked videos yet' : 'No videos found'}</p>
          </div>
        ) : (
          visiblePlaylist.map((item, filteredIndex) => {
            const isActive = item.id === currentVideoId
            const isSelected = selectedIds.has(item.id)
            const globalIndex = playlist.findIndex((p) => p.id === item.id)
            const compatibility = getCompatibilityInfo(item)
            const videoRating = ratings.get(ratingKey(item)) ?? { rating: 0, liked: false }

            return (
              <div
                key={item.id}
                ref={isActive ? activeRef : null}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleSelected(item.id)
                  } else {
                    handleItemClick(globalIndex)
                  }
                }}
                className={clsx(
                  'group flex items-start gap-2 px-3 py-2 cursor-pointer transition-colors border-b border-border/50',
                  'hover:bg-border/50',
                  isActive && 'playlist-item-active',
                  isSelected && 'bg-accent/10',
                )}
              >
                <VideoThumbnail item={item} compact />
                {/* Index / playing indicator */}
                <div className="w-5 flex-shrink-0 mt-0.5 text-right">
                  {isSelectionMode ? (
                    <button
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleSelected(item.id)
                      }}
                      className={clsx(
                        'inline-flex text-muted hover:text-bright',
                        isSelected && 'text-accent',
                      )}
                      title={isSelected ? 'Deselect' : 'Select'}
                    >
                      {isSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                    </button>
                  ) : isActive ? (
                    <PlayingDots />
                  ) : (
                    <span className="text-xs font-mono text-muted">
                      {filteredIndex + 1}
                    </span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={clsx(
                    'truncate pr-1 text-sm font-medium leading-tight',
                    isActive ? 'text-accent font-medium' : 'text-text',
                  )} title={cleanFilename(item.name)}>
                    {cleanFilename(item.name)}
                  </p>
                  <div className="mt-1 flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap">
                    {item.durationSeconds && (
                      <span className="text-xs font-mono text-muted">
                        {formatDuration(item.durationSeconds)}
                      </span>
                    )}
                    <span className="text-xs text-muted">
                      {formatBytes(item.size)}
                    </span>
                    <span className="text-xs text-muted">
                      {formatRelativeDate(item.modifiedAt)}
                    </span>
                    <CompatibilityBadge item={item} />
                    {compatibility.status !== 'likely' && (
                      <span className="text-xs text-muted">
                        {compatibility.container}
                      </span>
                    )}
                    {item.mediaMetadata?.width && item.mediaMetadata?.height && (
                      <span className="text-xs text-muted" title={`Metadata from ${item.mediaMetadata.source ?? 'provider'}`}>
                        {item.mediaMetadata.width}×{item.mediaMetadata.height}
                      </span>
                    )}
                    {item.mediaMetadata?.frameRate && (
                      <span className="text-xs text-muted">{item.mediaMetadata.frameRate.toFixed(2).replace(/\.00$/, '')} fps</span>
                    )}
                    {item.mediaMetadata?.videoCodec && (
                      <span className="rounded border border-border px-1 py-0.5 text-[10px] uppercase text-muted">
                        {item.mediaMetadata.videoCodec}
                      </span>
                    )}
                    {item.mediaMetadata?.audioCodec && (
                      <span className="text-[10px] uppercase text-muted">{item.mediaMetadata.audioCodec} audio</span>
                    )}
                  </div>
                  {!isSelectionMode && (
                    <div className="mt-1 flex h-5 items-center gap-0.5">
                      <div className="flex items-center" aria-label={`Rate ${cleanFilename(item.name)}`}>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <button
                            key={value}
                            onClick={(event) => {
                              event.stopPropagation()
                              void updateRating(item, { rating: videoRating.rating === value ? 0 : value })
                            }}
                            className={clsx('p-0.5 transition-colors', value <= videoRating.rating ? 'text-amber-300' : 'text-muted/45 hover:text-amber-200')}
                            title={`${value} star${value === 1 ? '' : 's'}`}
                            aria-label={`Rate ${value} star${value === 1 ? '' : 's'}`}
                          >
                            <Star size={12} fill={value <= videoRating.rating ? 'currentColor' : 'none'} />
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={(event) => {
                          event.stopPropagation()
                          void updateRating(item, { liked: !videoRating.liked })
                        }}
                        className={clsx('rounded p-1 transition-colors', videoRating.liked ? 'text-rose-400' : 'text-muted hover:text-rose-300')}
                        title={videoRating.liked ? 'Remove from Liked' : 'Add to Liked'}
                        aria-label={videoRating.liked ? 'Remove from Liked' : 'Add to Liked'}
                      >
                        <Heart size={13} fill={videoRating.liked ? 'currentColor' : 'none'} />
                      </button>
                      <div className="ml-auto flex items-center gap-0.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            setInspectedItem(item)
                          }}
                          className="btn-ghost rounded p-1 text-muted hover:text-bright"
                          title="View media details"
                          aria-label={`View details for ${cleanFilename(item.name)}`}
                        >
                          <Info size={13} />
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            addToQueueNext(item.id)
                          }}
                          className="btn-ghost rounded p-1"
                          title="Play next"
                        >
                          <CornerDownRight size={13} />
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            addToQueue(item.id)
                          }}
                          className="btn-ghost rounded p-1"
                          title="Add to queue"
                        >
                          <ListPlus size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {inspectedItem && (
        <MediaDetailsPanel item={inspectedItem} isOwner={isOwner} onHide={handleHideVideo} onClose={() => setInspectedItem(null)} />
      )}
    </div>
  )
}

export function MediaDetailsPanel({ item, isOwner = false, onHide, onClose }: { item: PlaylistItem; isOwner?: boolean; onHide?: (item: PlaylistItem) => Promise<void>; onClose: () => void }) {
  const compatibility = getCompatibilityInfo(item)
  const metadata = item.mediaMetadata
  const providerLabel = item.provider === 'google' ? 'Google Drive' : 'Microsoft OneDrive'
  const played = item.playCount ?? 0

  return (
    <div className="absolute inset-0 z-[80] flex flex-col bg-panel/98 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={`Details for ${cleanFilename(item.name)}`}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">Media details</p>
          <h2 className="mt-1 truncate text-base font-semibold text-bright">{cleanFilename(item.name)}</h2>
          <p className="mt-1 truncate text-xs text-muted">{item.name}</p>
        </div>
        <button onClick={onClose} className="btn-ghost rounded-lg p-2" aria-label="Close media details">
          <XCircle size={18} />
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        <div className="overflow-hidden rounded-xl border border-border bg-void shadow-xl">
          <div className="aspect-video w-full">
            <VideoThumbnail item={item} large />
          </div>
        </div>

        <section>
          <DetailHeading icon={<Cloud size={14} />} title="Source file" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <DetailCell label="Provider" value={providerLabel} />
            <DetailCell label="Folder" value="Room library" />
            <DetailCell label="Size" value={formatBytes(item.size)} />
            <DetailCell label="Duration" value={formatDuration(item.durationSeconds)} />
            <DetailCell label="MIME type" value={item.mimeType || 'Unknown'} wide />
            <DetailCell label="Modified" value={formatRelativeDate(item.modifiedAt)} wide />
          </div>
        </section>

        <section>
          <DetailHeading icon={<MonitorPlay size={14} />} title="Playback compatibility" />
          <div className="mt-2 rounded-lg border border-border bg-surface/60 p-3">
            <div className="flex items-center justify-between gap-3">
              <CompatibilityBadge item={item} />
              <span className="text-xs font-mono text-muted">{compatibility.container}</span>
            </div>
            <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-soft">
              {compatibility.notes.map((note) => <li key={note}>• {note}</li>)}
            </ul>
          </div>
        </section>

        <section>
          <DetailHeading icon={<Info size={14} />} title="Technical details" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <DetailCell label="Resolution" value={metadata?.width && metadata?.height ? `${metadata.width}×${metadata.height}` : 'Not reported'} />
            <DetailCell label="Frame rate" value={metadata?.frameRate ? `${metadata.frameRate.toFixed(2).replace(/\.00$/, '')} fps` : 'Not reported'} />
            <DetailCell label="Video" value={metadata?.videoCodec?.toUpperCase() ?? 'Not reported'} />
            <DetailCell label="Audio" value={metadata?.audioCodec?.toUpperCase() ?? 'Not reported'} />
            <DetailCell label="Bitrate" value={metadata?.bitrate ? `${Math.round(metadata.bitrate / 1000)} kbps` : 'Not reported'} />
            <DetailCell label="Metadata" value={metadata?.source ? `From ${metadata.source.replace('_', ' ')}` : 'Provider scan'} />
          </div>
        </section>

        <section>
          <DetailHeading icon={<PlayCircle size={14} />} title="This room’s play history" />
          <div className="mt-2 rounded-lg border border-border bg-surface/60 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text">Completed plays</span>
              <span className="font-mono text-lg font-semibold text-bright">{played}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3">
              <DetailCell label="First played" value={item.firstPlayedAt ? formatRelativeDate(item.firstPlayedAt) : 'Not played'} bare />
              <DetailCell label="Last played" value={item.lastPlayedAt ? formatRelativeDate(item.lastPlayedAt) : 'Not played'} bare />
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted"><CalendarDays size={12} /> History is scoped to the active room.</p>
          </div>
        </section>

        {isOwner && onHide && (
          <section className="rounded-xl border border-warning/30 bg-warning/5 p-3">
            <h3 className="text-xs font-semibold text-bright">Library visibility</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted">Hide this video from the active room’s library and playlist. Its cloud source file will never be deleted.</p>
            <button onClick={() => void onHide(item)} className="btn-ghost mt-3 rounded-lg border border-warning/35 px-3 py-2 text-xs text-warning hover:bg-warning/10">
              <EyeOff size={14} /> Hide from this room
            </button>
          </section>
        )}
      </div>
    </div>
  )
}

function DetailHeading({ icon, title }: { icon: React.ReactNode; title: string }) {
  return <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-soft">{icon}{title}</h3>
}

function DetailCell({ label, value, wide = false, bare = false }: { label: string; value: string; wide?: boolean; bare?: boolean }) {
  return (
    <div className={clsx(wide && 'col-span-2', !bare && 'rounded-lg border border-border bg-surface/55 p-2.5')}>
      <p className="text-[10px] uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 break-words text-xs text-text">{value}</p>
    </div>
  )
}

function CompatibilityBadge({
  item,
  compact = false,
}: {
  item: PlaylistItem
  compact?: boolean
}) {
  const compatibility = getCompatibilityInfo(item)
  const classNameByStatus: Record<CompatibilityStatus, string> = {
    likely: 'border-success/35 bg-success/10 text-success',
    warning: 'border-warning/45 bg-warning/10 text-warning',
    unsupported: 'border-danger/45 bg-danger/10 text-danger',
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none',
        classNameByStatus[compatibility.status],
      )}
      title={compatibility.notes.join(' ')}
    >
      {compact ? compatibility.container : compatibility.label}
    </span>
  )
}

function PlayingDots() {
  return (
    <div className="flex items-end gap-px h-4 justify-end">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-0.5 bg-accent rounded-full animate-pulse-slow"
          style={{
            height: `${8 + i * 4}px`,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  )
}
